/* Export der Karte (.map-Text) und der Entities einer geladenen BSP. */
import { useRef, useState } from 'react';
import { Dialog, DialogBody, DialogFoot } from '../dialog.tsx';
import { useT } from '../hooks.ts';
import { errCode } from './save.ts';
import type { Downloads } from './save.ts';

function TextToggle(props: { text: string; area: React.RefObject<HTMLTextAreaElement | null>; shown: boolean }) {
  return props.shown ? <textarea ref={props.area} readOnly spellCheck={false} value={props.text} onFocus={(e) => e.target.select()} /> : null;
}

export interface ExportInfo { brushes: number; ents: number; errors: string[]; warnings: string[] }

export function ExportDialog(props: { text: string; base: string; info: ExportInfo; dl: Downloads | null; onClose: () => void }) {
  const t = useT();
  const { text, base, info, dl } = props;
  const [msg, setMsg] = useState('');
  const [shown, setShown] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const save = async () => {
    if (!dl) return;
    try { await dl.save({ filename: base + '.txt', data: text }); setMsg(t('export.saved_txt')); } catch (err) {
      setMsg(errCode(err) === 'declined' ? t('export.cancelled') : t('export.save_failed_copy', { code: errCode(err) || t('export.error') }));
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setMsg(t('export.copied', { base })); } catch (err) { setShown(true); setMsg(t('export.clipboard_blocked')); }
  };
  return (
    <Dialog title={t('app.export')} onClose={props.onClose}>
      <DialogBody>
        <p className="small">{t('export.summary', { brushes: info.brushes, ents: info.ents, kb: Math.round(text.length / 1024) })}</p>
        {info.errors.length > 0 && <div className="warnbox">{t('export.errors', { count: info.errors.length }) + ' ' + info.errors.join(' · ')}</div>}
        {info.warnings.length > 0 && <div className="warnbox">{info.warnings.join(' · ')}</div>}
        <p className="small">{t('export.note')}</p>
        <div className="row">
          {dl && <button className="btn primary" onClick={save}>{t('export.save_txt')}</button>}
          <button className={dl ? 'btn' : 'btn primary'} onClick={copy}>{t('export.copy')}</button>
          <button className="btn" onClick={() => setShown(!shown)}>{t('export.show_text')}</button>
        </div>
        <p className="small" role="status">{msg}</p>
        <TextToggle text={text} area={area} shown={shown} />
      </DialogBody>
      <DialogFoot><button className="btn" onClick={props.onClose}>{t('ui.close')}</button></DialogFoot>
    </Dialog>
  );
}

export interface BspExportProps {
  base: string; entText: string; changed: boolean; entCount: number; texSwapped: boolean; dl: Downloads | null;
  /** erzeugt die ZIP-Daten mit der BSP; wird erst beim Speichern aufgerufen */
  makeZip(): Uint8Array;
  onClose: () => void;
}

export function BspExportDialog(props: BspExportProps) {
  const t = useT();
  const { dl, base } = props;
  const [msg, setMsg] = useState('');
  const [shown, setShown] = useState(false);
  const save = async (filename: string, data: () => string | Uint8Array, ok: string) => {
    if (!dl) return;
    try { await dl.save({ filename, data: data() }); setMsg(ok); } catch (err) {
      setMsg(errCode(err) === 'declined' ? t('export.cancelled') : t('export.save_failed_text', { code: errCode(err) || t('export.error') }));
    }
  };
  return (
    <Dialog title={t('export.bsp_title')} onClose={props.onClose}>
      <DialogBody>
        <p className="small">{t(props.changed ? 'export.bsp_summary_changed' : 'export.bsp_summary_same', { count: props.entCount })}</p>
        <p className="small">{t('export.bsp_note')}</p>
        <div className="row">
          {dl && <>
            <button className="btn primary" onClick={() => save(base + '.zip', props.makeZip, t(props.texSwapped ? 'export.bsp_saved_tex' : 'export.bsp_saved', { base }))}>{t('export.save_bsp')}</button>
            <button className="btn" onClick={() => save(base + '.txt', () => props.entText, t('export.ents_saved'))}>{t('export.save_ents')}</button>
          </>}
          <button className="btn" onClick={() => setShown(!shown)}>{t('export.show_text')}</button>
        </div>
        <p className="small" role="status">{msg}</p>
        {shown && <textarea readOnly spellCheck={false} value={props.entText} onFocus={(e) => e.target.select()} />}
      </DialogBody>
      <DialogFoot><button className="btn" onClick={props.onClose}>{t('ui.close')}</button></DialogFoot>
    </Dialog>
  );
}
